/**
 * Where the Hierarchy tab's Filters sidebar sits and whether it starts open. Pure so the rules are
 * testable; the tab feeds it the container width (not the viewport: the V1 card and V2 module are
 * narrow even on a wide screen).
 */
import type { FilterOption } from '@shared/flow/account-hierarchy-deal-model';

/** Container width from which the sidebar docks beside the canvas by default. */
export const SIDEBAR_WIDE_PX = 900;
/** A multi-select with more options than this gets a search box. */
export const SIDEBAR_SEARCH_THRESHOLD = 6;

export interface SidebarContext {
  fullscreen: boolean;
  /** Measured width of the tab container, null until measured. */
  containerWidth: number | null;
}

/** Docked beside the canvas, or a side sheet over the right part of the tab (narrow container, never full screen). */
export function sidebarPlacement({ fullscreen, containerWidth }: SidebarContext): 'docked' | 'sheet' {
  if (fullscreen || containerWidth === null) return 'docked';
  return containerWidth >= SIDEBAR_WIDE_PX ? 'docked' : 'sheet';
}

/** The rep's remembered choice per view (null = never chosen). */
export interface SidebarChoice {
  inline: boolean | null;
  fullscreen: boolean | null;
}

/** A choice wins; otherwise open in full screen and in a wide container, collapsed in a narrow one. */
export function resolveSidebarOpen(choice: SidebarChoice, ctx: SidebarContext): boolean {
  const chosen = ctx.fullscreen ? choice.fullscreen : choice.inline;
  if (chosen !== null) return chosen;
  return sidebarPlacement(ctx) === 'docked';
}

export function filterOptionsByQuery(options: readonly FilterOption[], query: string): FilterOption[] {
  const q = query.trim().toLowerCase();
  return q === '' ? [...options] : options.filter(o => o.label.toLowerCase().includes(q));
}
